import {useSuspenseQuery, useQueryClient} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import {useAlive} from '@github-ui/use-alive'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useMemo} from 'react'
import {
  FindingsQueryDataValidator,
  type Finding,
  type FindingsPayload,
  type FindingsQueryData,
  type Freshness,
  type MergeReadiness,
} from './types'

interface UseFindingsOptions {
  pullRequestBasePath: string
  aliveChannel?: string | null
}

interface UseFindingsResult {
  findings: Finding[]
  freshness: Freshness
  headSha: string | null
  modelsUrl: string | null
  mergeReadiness: MergeReadiness | null
  showFeedbackBanner: boolean
  feedbackBannerDismissPath: string | null
  isNotFound: boolean
  sweAgentEnabled: boolean
  dotcomChatEnabled: boolean
  canApplySuggestion: boolean
  maxFindingBatchSize: number
}

const DEFAULT_RETRY_AFTER_SECONDS = 10
const MIN_RETRY_AFTER_SECONDS = 1

const getRetryAfterMs = (
  response: Response | null,
  defaultRetryAfterSeconds: number = DEFAULT_RETRY_AFTER_SECONDS,
): number => {
  const retryAfterHeader = response?.headers.get('Retry-After')
  const parsedRetryAfter = retryAfterHeader === null ? Number.NaN : Number(retryAfterHeader)
  const retryAfterSeconds = Number.isFinite(parsedRetryAfter)
    ? Math.max(MIN_RETRY_AFTER_SECONDS, parsedRetryAfter)
    : defaultRetryAfterSeconds

  return retryAfterSeconds * 1000
}

const FINDINGS_QUERY_KEY_PREFIX = 'code-review-findings' as const

export const findingsQueryKey = (pullRequestBasePath: string) => {
  return [FINDINGS_QUERY_KEY_PREFIX, pullRequestBasePath] as const
}

export const getRefetchInterval = (queryData: FindingsQueryData | undefined): number | false => {
  const freshness = queryData?.payload?.currentFreshness
  if (freshness && freshness !== 'current') {
    return queryData?.retryAfterMs ?? DEFAULT_RETRY_AFTER_SECONDS * 1000
  }
  // Poll on 404 if the server sent Retry-After (findings not ready yet)
  if (queryData?.payload === null && queryData.retryAfterMs > 0) {
    return queryData.retryAfterMs
  }
  return false
}

export const useFindings = ({pullRequestBasePath, aliveChannel}: UseFindingsOptions): UseFindingsResult => {
  const {sendAnalyticsEvent} = useAnalytics()
  const queryClient = useQueryClient()

  const persister = useMemo(
    () =>
      createPersister({
        validator: FindingsQueryDataValidator,
        sendAnalyticsEvent,
      }),
    [sendAnalyticsEvent],
  )

  const handleAliveUpdate = useCallback(
    (data: {event_updates?: {findings_updated?: boolean}}) => {
      if (data.event_updates?.findings_updated) {
        void queryClient.invalidateQueries({queryKey: findingsQueryKey(pullRequestBasePath)})
      }
    },
    [queryClient, pullRequestBasePath],
  )

  useAlive(aliveChannel, handleAliveUpdate)

  const {data} = useSuspenseQuery({
    queryKey: findingsQueryKey(pullRequestBasePath),
    queryFn: async (): Promise<FindingsQueryData> => {
      const response = await verifiedFetchJSON(`${pullRequestBasePath}/code_review_findings?view=pr`)
      if (response.status === 404) {
        return {payload: null, retryAfterMs: getRetryAfterMs(response, 0)}
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch findings (${response.status})`)
      }
      const payload = (await response.json()) as FindingsPayload
      return {payload, retryAfterMs: getRetryAfterMs(response)}
    },
    refetchInterval: query => getRefetchInterval(query.state.data),
    persister,
  })

  const payload = data.payload

  return {
    findings: payload?.findings ?? [],
    freshness: payload?.currentFreshness ?? 'pending',
    headSha: payload?.headSha ?? null,
    modelsUrl: payload?.modelsUrl ?? null,
    mergeReadiness: payload?.mergeReadiness ?? null,
    showFeedbackBanner: payload?.showFeedbackBanner ?? false,
    feedbackBannerDismissPath: payload?.feedbackBannerDismissPath ?? null,
    isNotFound: data.payload === null && data.retryAfterMs === 0,
    sweAgentEnabled: payload?.sweAgentEnabled ?? false,
    dotcomChatEnabled: payload?.dotcomChatEnabled ?? false,
    canApplySuggestion: payload?.canApplySuggestion ?? false,
    maxFindingBatchSize: payload?.maxFindingBatchSize ?? 1,
  }
}
